"use client";

import { type createAuthClient, isAuthApiError } from "@neondatabase/auth/next";

/**
 * Workaround for a mismatch between @neondatabase/auth 0.5.0-beta and
 * @neondatabase/auth-ui 0.3.0-beta. Delete this file, and its call in
 * `client.ts`, once the two packages agree on an error shape.
 *
 * Signing in with an unverified email should land on the email verification
 * view. auth-ui routes there when the rejection carries `error.error.code ===
 * "EMAIL_NOT_VERIFIED"`, but the Neon client normalizes every failed request
 * into a Supabase-shaped error that holds `code` at the top level instead, so
 * the branch never matches and the user is stranded on sign-in with a toast.
 */

type AuthClient = ReturnType<typeof createAuthClient>;

/** Neon's normalized code for an unverified email. Not exported by the package. */
const NEON_AUTH_EMAIL_UNVERIFIED = "email_not_confirmed";

/** The better-auth code auth-ui branches on. */
const BETTER_AUTH_EMAIL_UNVERIFIED = "EMAIL_NOT_VERIFIED";

/** Restores the nested error code on a sign-in refused for an unverified email. */
export function withVerificationErrorCode(client: AuthClient): AuthClient {
  // Read once and held as a value. Every property read on the client resolves
  // to a URL path segment, so touching `.bind` would request
  // /sign-in/email/bind rather than returning a bound function.
  const signInWithEmail = client.signIn.email;

  async function signInPreservingErrorCode(
    ...args: Parameters<typeof signInWithEmail>
  ) {
    try {
      return await signInWithEmail(...args);
    } catch (err) {
      if (isAuthApiError(err) && err.code === NEON_AUTH_EMAIL_UNVERIFIED) {
        Object.assign(err, {
          error: { code: BETTER_AUTH_EMAIL_UNVERIFIED, message: err.message },
        });
      }
      throw err;
    }
  }

  // Delegating proxies rather than assignment: the client is a dynamic path
  // proxy with no setter, so writing to it silently does nothing.
  const signIn = new Proxy(client.signIn, {
    get: (target, prop, receiver) =>
      prop === "email"
        ? signInPreservingErrorCode
        : Reflect.get(target, prop, receiver),
  });

  return new Proxy(client, {
    get: (target, prop, receiver) =>
      prop === "signIn" ? signIn : Reflect.get(target, prop, receiver),
  });
}
